/**
 * TanStack Query bindings for the /admin/usage surface: the global model
 * switcher (GetChatModelSettings / UpdateChatModelSettings) and the token
 * consumption views (GetTokenUsageSummary / ListUserTokenUsage). All four
 * RPCs live on shanks AgentService (browser → sabaody → shanks) and are
 * admin-gated server-side; RequireAdminRoute is only the UX gate.
 */ import { Timestamp } from '@bufbuild/protobuf';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { agentClient } from '../../../lib/connect/clients';
export const MODEL_SETTINGS_KEY = 'admin:model-settings';
export const USAGE_SUMMARY_KEY = 'admin:usage-summary';
export const USAGE_USERS_KEY = 'admin:usage-users';
const RANGE_MS = {
    '24h': 24 * 60 * 60 * 1000,
    '7d': 7 * 24 * 60 * 60 * 1000,
    '30d': 30 * 24 * 60 * 60 * 1000
};
/** Half-open [start, now) — `end` is omitted so the backend uses "now". */ function rangeStart(range) {
    return Timestamp.fromDate(new Date(Date.now() - RANGE_MS[range]));
}
/** Stored + effective model settings and the selectable catalog. */ export function useModelSettings() {
    return useQuery({
        queryKey: [
            MODEL_SETTINGS_KEY
        ],
        queryFn: ()=>agentClient.getChatModelSettings({})
    });
}
/**
 * Switches the globally-active chat/subagent models. Invalidate-on-success
 * (not optimistic): a global, rare, admin-gated switch where briefly showing
 * not-yet-true state is worse than a refetch.
 */ export function useUpdateModelSettings() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>agentClient.updateChatModelSettings(args),
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    MODEL_SETTINGS_KEY
                ]
            });
            toast.success('Active model updated — takes effect on new chats');
        },
        onError: (e)=>toast.error(e instanceof Error ? e.message : 'Failed to update model')
    });
}
/** Global consumption: totals, per-day series, breakdowns, active users. */ export function useUsageSummary(range) {
    return useQuery({
        queryKey: [
            USAGE_SUMMARY_KEY,
            range
        ],
        refetchInterval: 60000,
        queryFn: ()=>agentClient.getTokenUsageSummary({
                start: rangeStart(range)
            })
    });
}
/** Paginated per-user consumption — the usage table data source. */ export function useUserUsage(range, search, page, pageSize = 25) {
    return useQuery({
        queryKey: [
            USAGE_USERS_KEY,
            range,
            search,
            page,
            pageSize
        ],
        placeholderData: keepPreviousData,
        queryFn: async ()=>{
            const resp = await agentClient.listUserTokenUsage({
                start: rangeStart(range),
                search,
                limit: pageSize,
                offset: (page - 1) * pageSize
            });
            return {
                users: resp.users,
                total: resp.totalCount ?? 0
            };
        }
    });
}
/**
 * Token counts arrive as int64 (bigint on the wire). Compact display —
 * "12.4K" / "1.2M" — with the exact value available via tooltips.
 */ export function formatTokens(v) {
    const n = Number(v ?? 0);
    return Intl.NumberFormat('en-US', {
        notation: 'compact',
        maximumFractionDigits: 1
    }).format(n);
}
/** Exact count with thousands separators, for title attrs / tooltips. */ export function formatTokensExact(v) {
    return Number(v ?? 0).toLocaleString('en-US');
}
